import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'

type Ph = 'empty' | 'open' | 'hold' | 'through' | 'inside' | 'reset'

/**
 * ⑮ ワープホール→異空間 — 何もない闇に突然ホールが開き、
 * 縁のまわりを波のような透明パーティクルが伝う。くぐると別空間
 * （時の狭間: 朱の鳥居列と浮かぶ畳・金の木片が漂う和の異空間）へ。
 * MRデモ(portal.html)の演出をショーケース単体で確認できる版。
 */
export class Proto15 extends ProtoBase {
  readonly title = '15. ワープホール→異空間'
  readonly desc = '無から空間が開くワープホール（波状の透明パーティクル演出）→ くぐると朱鳥居の異空間へ'

  private ph: Ph = 'empty'
  private phT = 0
  private membrane!: THREE.Mesh
  private membraneMat!: THREE.ShaderMaterial
  private ripGeo!: THREE.BufferGeometry
  private ripSeed: { ang: number; phase: number; speed: number }[] = []
  private beyond = new THREE.Group()
  private flashMat!: THREE.MeshBasicMaterial
  private insideU = 0

  private static readonly HOLE = new THREE.Vector3(0, 1.7, -6)
  private static readonly RIP_N = 480

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.view.driftEnabled = false
    this.ctx.scene.background = new THREE.Color(0x04060b)
    this.ctx.scene.fog = null
    this.ctx.setCaption('……何もない。だが、空間がふるえている')
    this.ctx.view.setPose(new THREE.Vector3(0, 1.7, 4), Proto15.HOLE.clone())

    // ── ワープホール膜（異次元の裂け目シェーダ・開閉uniform付き）──
    this.membraneMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      uniforms: { uT: { value: 0 }, uOpen: { value: 0 } },
      vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.); }`,
      fragmentShader: `
        uniform float uT; uniform float uOpen; varying vec2 vUv;
        float hash(vec2 q){ return fract(sin(dot(q, vec2(12.9898,78.233)))*43758.5453); }
        void main(){
          vec2 p = vUv*2.-1.;
          float r = length(p);
          if(r>1.) discard;
          float a = atan(p.y,p.x);
          float t = uT;
          float depth = fract(-t*0.7 + 0.35/(r+0.12));
          float tunnel = smoothstep(0.0,0.22,depth)*smoothstep(1.0,0.5,depth);
          float arm = sin(a*3.0 + t*2.8 - 2.4/(r+0.15));
          float armGlow = smoothstep(0.55,0.95,arm);
          vec3 col = mix(vec3(0.04,0.06,0.22), vec3(0.45,0.18,0.85), tunnel);
          col += vec3(0.30,0.80,1.00) * armGlow * (1.0-r) * 0.8;
          col += vec3(1.0,0.9,0.75) * smoothstep(0.28,0.0,r) * (0.5+0.25*sin(t*5.0));
          float rim = smoothstep(0.10,0.0,abs(r-0.93));
          col += vec3(1.0,0.85,0.5) * rim * 2.0;
          float sp = hash(floor(p*44.0 + t*9.0));
          col += vec3(1.0) * step(0.995, sp) * 0.7;
          float alpha = (smoothstep(1.0,0.12,r)*0.95 + rim) * uOpen;
          gl_FragColor = vec4(col, alpha);
        }`,
    })
    this.membrane = new THREE.Mesh(new THREE.CircleGeometry(1.45, 56), this.membraneMat)
    this.membrane.position.copy(Proto15.HOLE)
    this.membrane.scale.setScalar(0.001)
    this.group.add(this.membrane)

    // ── 波のような透明パーティクル（縁から同心円状に広がる波紋）──
    this.ripGeo = new THREE.BufferGeometry()
    this.ripGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(Proto15.RIP_N * 3), 3))
    this.ripGeo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(Proto15.RIP_N * 3), 3))
    this.ripSeed = Array.from({ length: Proto15.RIP_N }, (_, i) => ({
      ang: (i / Proto15.RIP_N) * Math.PI * 2 * 5,
      phase: (i * 0.618) % 1,
      speed: 0.22 + (i % 9) * 0.03,
    }))
    const rip = new THREE.Points(this.ripGeo, new THREE.PointsMaterial({
      size: 0.05, vertexColors: true, transparent: true, opacity: 0.9,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }))
    rip.frustumCulled = false
    this.group.add(rip)

    // ── 別空間（時の狭間）: 朱鳥居の列・浮かぶ畳・金の木片 ──
    this.beyond.visible = false
    this.group.add(this.beyond)
    {
      const shu = new THREE.MeshStandardMaterial({ color: 0xa53f2b, roughness: 0.55 })
      for (let i = 0; i < 9; i++) {
        const z = -4 - i * 7
        const s = 1 + i * 0.06
        const torii = new THREE.Group()
        for (const sx of [-1.6, 1.6]) {
          const post = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.15, 3.4, 8), shu)
          post.position.set(sx, 1.7, 0)
          torii.add(post)
        }
        const kasagi = new THREE.Mesh(new THREE.BoxGeometry(4.4, 0.26, 0.3), shu)
        kasagi.position.y = 3.45
        torii.add(kasagi)
        const nuki = new THREE.Mesh(new THREE.BoxGeometry(3.7, 0.18, 0.22), shu)
        nuki.position.y = 2.7
        torii.add(nuki)
        torii.position.set(Math.sin(i * 0.55) * 2.2, 0, z)
        torii.scale.setScalar(s)
        this.beyond.add(torii)
      }
      const ground = new THREE.Mesh(
        new THREE.CircleGeometry(70, 48),
        new THREE.MeshStandardMaterial({ color: 0x141020, roughness: 0.9, metalness: 0.25 }),
      )
      ground.rotation.x = -Math.PI / 2
      this.beyond.add(ground)
      for (let i = 0; i < 6; i++) {
        const tat = new THREE.Mesh(
          new THREE.BoxGeometry(0.91 * 2, 0.12, 1.82 * 2),
          new THREE.MeshStandardMaterial({ color: 0x8fbf7d, roughness: 0.8 }),
        )
        tat.position.set(Math.sin(i * 2.4) * 9, 2.2 + Math.sin(i * 1.3) * 1.6, -8 - i * 8.5)
        tat.rotation.y = i * 0.7
        this.beyond.add(tat)
      }
      const chipGeo = new THREE.BufferGeometry()
      const cp: number[] = []
      for (let i = 0; i < 320; i++) {
        cp.push((Math.random() - 0.5) * 44, Math.random() * 12, -Math.random() * 66)
      }
      chipGeo.setAttribute('position', new THREE.Float32BufferAttribute(cp, 3))
      this.beyond.add(new THREE.Points(chipGeo, new THREE.PointsMaterial({
        color: 0xf0c070, size: 0.22, transparent: true, opacity: 0.85,
        blending: THREE.AdditiveBlending, depthWrite: false,
      })))
      const warm = new THREE.PointLight(0xffd2a0, 90, 80)
      warm.position.set(0, 8, -20)
      this.beyond.add(warm)
    }

    // フラッシュ板（通過時）
    this.flashMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0, depthTest: false, depthWrite: false,
    })
    const flash = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), this.flashMat)
    flash.position.set(0, 1.7, -2)
    flash.renderOrder = 999
    this.group.add(flash)

    this.ph = 'empty'
    this.phT = 0
  }

  update(dt: number): void {
    this.elapsed += dt
    this.phT += dt
    this.membraneMat.uniforms.uT.value += dt
    this.membrane.rotation.z += dt * 0.45
    if (this.flashMat.opacity > 0) this.flashMat.opacity = Math.max(0, this.flashMat.opacity - dt * 1.5)

    // 波紋パーティクル更新（膜の開き具合に連動）
    const open = this.membraneMat.uniforms.uOpen.value as number
    const pos = this.ripGeo.getAttribute('position') as THREE.BufferAttribute
    const col = this.ripGeo.getAttribute('color') as THREE.BufferAttribute
    const t = this.membraneMat.uniforms.uT.value as number
    for (let i = 0; i < Proto15.RIP_N; i++) {
      const s = this.ripSeed[i]
      const k = (t * s.speed + s.phase) % 1 // 0=縁 → 1=外へ
      const r = (1.5 + k * 2.4) * Math.max(0.001, this.membrane.scale.x)
      const wobble = Math.sin(k * Math.PI * 4 - t * 2.4) * 0.14 // 波のうねり
      pos.setXYZ(i,
        Proto15.HOLE.x + Math.cos(s.ang) * r,
        Proto15.HOLE.y + Math.sin(s.ang) * r,
        Proto15.HOLE.z + wobble)
      const fade = (1 - k) * 0.6 * open
      col.setXYZ(i, 0.65 * fade, 0.85 * fade, 1.0 * fade)
    }
    pos.needsUpdate = true
    col.needsUpdate = true

    switch (this.ph) {
      case 'empty':
        if (this.phT > 1.6) {
          this.ph = 'open'
          this.phT = 0
          this.ctx.sfx.play('drum')
          this.ctx.setCaption('——空間が、ひらく！')
        }
        break
      case 'open': {
        // 弾性イーズで「空間がひらいていく」
        const u = Math.min(1, this.phT / 1.8)
        const e = 1 - Math.pow(2, -10 * u) * Math.cos(u * 9)
        this.membrane.scale.setScalar(Math.max(0.001, e))
        this.membraneMat.uniforms.uOpen.value = Math.min(1, this.phT / 1.2)
        if (u >= 1) {
          this.ph = 'hold'
          this.phT = 0
          this.ctx.setCaption('ワープホールじゃ。……よし、入るぞ')
        }
        break
      }
      case 'hold':
        if (this.phT > 2.2) {
          this.ph = 'through'
          this.phT = 0
          this.ctx.sfx.play('whoosh')
        }
        break
      case 'through': {
        // ホールへ吸い込まれる（等加速の短いドリー）
        const u = Math.min(1, this.phT / 2.2)
        const e = u * u
        const z = 4 + (Proto15.HOLE.z - 1.2 - 4) * e
        this.ctx.view.setPose(new THREE.Vector3(0, 1.7, z), Proto15.HOLE.clone().add(new THREE.Vector3(0, 0, -4)))
        if (z < Proto15.HOLE.z) {
          this.ph = 'inside'
          this.phT = 0
          this.insideU = 0
          this.flashMat.opacity = 1
          this.beyond.visible = true
          this.membrane.visible = false
          this.ctx.scene.background = new THREE.Color(0x2a1630)
          this.ctx.scene.fog = new THREE.Fog(0x2a1630, 22, 75)
          this.ctx.sfx.play('bell')
          this.ctx.setCaption('ホールの先は別空間——時の狭間。朱の鳥居が、道をつくっておる')
        }
        break
      }
      case 'inside': {
        // 異空間: 鳥居の道をゆっくり進む
        this.insideU += dt
        const z = -2 - this.insideU * 1.6
        this.ctx.view.setPose(
          new THREE.Vector3(Math.sin(this.insideU * 0.35) * 1.2, 1.7, z),
          new THREE.Vector3(0, 1.9, z - 8),
        )
        if (this.phT > 14) {
          this.ph = 'reset'
          this.phT = 0
          this.flashMat.opacity = 1
          this.ctx.sfx.play('drum')
        }
        break
      }
      case 'reset':
        // 元の闇へ戻ってループ
        this.beyond.visible = false
        this.membrane.visible = true
        this.membrane.scale.setScalar(0.001)
        this.membraneMat.uniforms.uOpen.value = 0
        this.ctx.scene.background = new THREE.Color(0x04060b)
        this.ctx.scene.fog = null
        this.ctx.view.setPose(new THREE.Vector3(0, 1.7, 4), Proto15.HOLE.clone())
        this.ctx.setCaption('……戻ってきた。もう一度、空間がふるえはじめる')
        this.ph = 'empty'
        this.phT = 0
        break
    }
  }

  protected onDispose(): void {
    this.ctx.view.driftEnabled = true
    this.ctx.scene.fog = null
  }
}
